import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { LifeBuoy, X } from "lucide-react";

const ISSUE_TYPES = [
  { value: "account_access", label: "Account access" },
  { value: "registration", label: "Registration" },
  { value: "documents", label: "Documents" },
  { value: "payments", label: "Payments" },
  { value: "technical", label: "Technical issue" },
  { value: "other", label: "Other" },
];

const EMPTY = { issue_type: "", description: "", name: "", email: "", captcha_answer: "", website: "" };

export const SupportWidget = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);

  const loadChallenge = useCallback(async () => {
    try { const { data } = await api.get("/support/challenge"); setChallenge(data); }
    catch { setChallenge(null); }
  }, []);

  const openWidget = useCallback(() => {
    setForm(EMPTY);
    setFile(null);
    setOpen(true);
    if (!user) loadChallenge();
  }, [user, loadChallenge]);

  useEffect(() => {
    const h = () => openWidget();
    window.addEventListener("open-support", h);
    return () => window.removeEventListener("open-support", h);
  }, [openWidget]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.issue_type) return toast.error("Please choose an issue type.");
    if (form.description.trim().length < 5) return toast.error("Please describe your issue.");
    if (!user && (!form.email || !form.email.includes("@"))) return toast.error("Please enter a valid email.");
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("issue_type", form.issue_type);
      fd.append("description", form.description);
      fd.append("website", form.website); // honeypot
      if (!user) {
        fd.append("name", form.name);
        fd.append("email", form.email);
        fd.append("captcha_token", challenge?.token || "");
        fd.append("captcha_answer", form.captcha_answer);
      }
      if (file) fd.append("screenshot", file);
      const { data } = await api.post("/support/ticket", fd);
      toast.success(`Request received (ref ${data.ticket_id}). We'll reply within one business day.`);
      setOpen(false);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not send your request. Please try again.");
      if (!user) loadChallenge();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        data-testid="support-fab"
        onClick={openWidget}
        aria-label="Contact support"
        className="fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full bg-slate-900 px-4 py-3 text-sm font-semibold text-white shadow-lg transition-transform hover:scale-105 hover:bg-slate-800"
      >
        <LifeBuoy className="h-4 w-4" /> Support
      </button>

      {open && (
        <div
          data-testid="support-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <h3 className="font-display text-lg font-bold text-slate-900">Contact support</h3>
              <button data-testid="support-close" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={submit} className="flex-1 space-y-4 overflow-auto px-5 py-4">
              {user ? (
                <div data-testid="support-identity" className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
                  Signed in as <span className="font-semibold text-slate-700">{user.name}</span> ({user.email}) &middot; {user.role}
                </div>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Your name</label>
                    <input data-testid="support-name" value={form.name} onChange={set("name")} className="mt-1 h-10 w-full rounded-md border border-slate-300 px-3 text-sm" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Email <span className="text-red-500">*</span></label>
                    <input data-testid="support-email" type="email" value={form.email} onChange={set("email")} className="mt-1 h-10 w-full rounded-md border border-slate-300 px-3 text-sm" />
                  </div>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-slate-700">Issue type <span className="text-red-500">*</span></label>
                <select data-testid="support-issue-type" value={form.issue_type} onChange={set("issue_type")} className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                  <option value="">Select&hellip;</option>
                  {ISSUE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Description <span className="text-red-500">*</span></label>
                <textarea data-testid="support-description" value={form.description} onChange={set("description")} rows={4} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder="How can we help?" />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Screenshot <span className="text-xs font-normal text-slate-400">(optional)</span></label>
                <input data-testid="support-screenshot" type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1 block w-full text-sm text-slate-600" />
              </div>

              {/* honeypot — hidden from real users */}
              <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden="true" />

              {!user && challenge && (
                <div>
                  <label className="text-sm font-medium text-slate-700">{challenge.question} <span className="text-red-500">*</span></label>
                  <input data-testid="support-captcha-answer" value={form.captcha_answer} onChange={set("captcha_answer")} className="mt-1 h-10 w-full rounded-md border border-slate-300 px-3 text-sm" />
                </div>
              )}

              <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                For your security, do not include passwords, SSNs, tax IDs, bank-account details, or full card numbers. We will never ask for them.
              </p>

              <button data-testid="support-submit" type="submit" disabled={submitting} className="w-full rounded-md bg-slate-900 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60">
                {submitting ? "Sending\u2026" : "Send request"}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};
